import {
  compose,
  flattenProp,
  withState,
  withHandlers,
  withStateHandlers,
} from 'recompose';
import { injectIntl } from 'react-intl';
import { addProps, addIntlProps } from 'utils/recompose';
import { connect } from 'react-redux';
import { mini } from 'reducers/dashboard/selectors';
import {
  template,
  designPackageForTemplate,
  templateQuantity,
  templatePriority,
  templateStatus,
  templatePriorities,
  submittedTemplateQuantity,
  selectedTemplateId,
  swappedTemplateId,
  duplicateTemplateId,
  charmsForTemplate,
} from 'reducers/templates/selectors';
import {
  unsubmittedQuota,
  modelWorkingStateDenormalized,
} from 'reducers/models/selectors';
import {
  addTemplateToWorkingState,
  removeTemplateFromWorkingState,
  updateModelWorkingState,
  swapTemplates,
} from 'reducers/models/actions';
import {
  setSelectedTemplateId,
  setDuplicateTemplateId,
  setSwappedTemplateId,
} from 'reducers/templates/actions';
import combineSelectors from 'utils/combineSelectors';
import formatCurrency from 'utils/formatCurrency';
import messages from './messages';
import removeTemplate from 'utils/removeTemplate';
import { priceBands } from 'reducers/models/selectors';
import { differenceWith, isEqual } from 'lodash';

const mapStateToProps = combineSelectors({
  unsubmittedQuota,
  template,
  designPackageForTemplate,
  templateQuantity,
  templatePriority,
  templateStatus,
  templatePriorities,
  submittedTemplateQuantity,
  modelWorkingStateDenormalized,
  selectedTemplateId,
  swappedTemplateId,
  duplicateTemplateId,
  priceBands,
  charmsForTemplate,
  mini,
});

export const priceBand = ({ priceBands, priceBandId }) =>
  priceBands.find(priceBand => priceBand.id === priceBandId) || {};
export const priceBandMax = ({ priceBand }) => priceBand.max;
export const priceBandMin = ({ priceBand }) => priceBand.min;

const formattedTotal = formatCurrency({
  priceProp: 'totalPrice',
  currencyProp: 'currency',
});

const formattedDesignPrice = formatCurrency({
  priceProp: 'designPrice',
  currencyProp: 'currency',
});

const formattedPriceBandMax = formatCurrency({
  priceProp: 'priceBandMax',
  currencyProp: 'currency',
});

const formattedPriceBandMin = formatCurrency({
  priceProp: 'priceBandMin',
  currencyProp: 'currency',
});

export const formattedPriceBand = ({
  formattedPriceBandMin,
  formattedPriceBandMax,
  priceBand,
}) => {
  if (priceBand.max < 1000000) {
    return `(${formattedPriceBandMin} - ${formattedPriceBandMax})`;
  } else if (priceBand.max >= 1000000) {
    return `(${formattedPriceBandMin}+)`;
  }
};

export const renderBmwOrMotorradSelection = ({ selectedDesignOption, mini }) =>
  !!selectedDesignOption && !mini;

export const renderMiniSelection = ({ selectedDesignOption, mini }) =>
  !!selectedDesignOption && mini;

export const closeDesign = () => () => ({ selectedDesignOption: null });

export const closeSelection = ({
  closeDesign,
  swappedTemplateId,
  template,
  swapTemplates,
  isDuplicate,
  addTemplateToWorkingState,
  setSelectedTemplateId,
  setDuplicateTemplateId,
  setSwappedTemplateId,
  duplicateTemplateId,
}) => () => {
  if (isDuplicate && template.modelId !== swappedTemplateId) {
    addTemplateToWorkingState(
      template.modelId,
      swappedTemplateId,
      duplicateTemplateId,
    );
  } else if (!!swappedTemplateId) {
    swapTemplates(template.id, swappedTemplateId);
  }
  setDuplicateTemplateId(null);
  setSelectedTemplateId(null);
  setSwappedTemplateId(null);
  closeDesign();
};

export const onClickOrTouch = () => selectedDesignOption => ({
  selectedDesignOption,
});

export const expandMiniMore = () => () => ({
  selectedDesignOption: 'more',
});

export const collapseAndOpenDesign = ({ onClickOrTouch, collapse }) => () => {
  onClickOrTouch('paintOption');
  collapse();
};

export const openDetail = ({ toggleDropdown, setDetailOpenState }) => () => {
  toggleDropdown();
  setDetailOpenState(open => !open);
};

export const closeDetail = ({ setDetailOpenState }) => () =>
  setDetailOpenState(open => !open);

export const openMultiplePriorities = ({ multiplePriorities }) => () => ({
  multiplePriorities: true,
});

export const closeMultiplePriorities = ({ multiplePriorities }) => () => ({
  multiplePriorities: false,
});

export const hasSubmitted = ({ submittedTemplateQuantity }) =>
  submittedTemplateQuantity > 0;

export const toggleSubmissionNote = ({ submissionNotificationOpen }) => () => {
  return { submissionNotificationOpen: !submissionNotificationOpen };
};

export const displayPrioritiesExpanded = ({
  templatePriority,
  multiplePriorities,
}) => {
  if (templatePriority === 'Multiple' || multiplePriorities) {
    return true;
  } else {
    return false;
  }
};

export const inReview = ({ templateStatus }) => templateStatus === 'inReview';
export const inCart = ({ templateStatus }) => templateStatus === 'inCart';
export const zeroQuantity = ({ templateQuantity }) => templateQuantity === 0;

export const toggleDropdown = ({ templateId, setSelectedTemplateId }) => () =>
  setSelectedTemplateId(templateId);

export const duplicateTemplate = ({
  template,
  setDuplicateTemplateId,
  toggleDropdown,
}) => () => {
  toggleDropdown();
  setDuplicateTemplateId(template.id);
};

export const removeDuplicate = ({ setDuplicateTemplateId }) => () => {
  setDuplicateTemplateId(null);
};

export const isDropdownOpen = ({ selectedTemplateId, templateId }) =>
  selectedTemplateId === templateId;

export const dropDownModelId = ({ isDuplicate, modelId }) =>
  isDuplicate ? null : modelId;

export const dropdownDisabled = ({ isDuplicate, templateStatus }) =>
  isDuplicate || templateStatus === 'inCart';

const expand = ({ expanded }) => () => {
  return { expanded: !expanded };
};

const collapse = () => () => {
  return { expanded: false };
};

export const primaryOptions = () => [
  'paintOption',
  'interiorOption',
  'wheelOption',
];

export const secondaryOptions = ({
  primaryOptions,
  designPackageForTemplate,
}) => {
  return differenceWith(
    designPackageForTemplate.optionKeys,
    primaryOptions,
    isEqual,
  );
};

export default compose(
  injectIntl,
  addIntlProps({
    messages,
    propNames: ['currency', 'duplicate'],
  }),
  connect(mapStateToProps, {
    addTemplateToWorkingState,
    removeTemplateFromWorkingState,
    updateModelWorkingState,
    setSelectedTemplateId,
    setDuplicateTemplateId,
    setSwappedTemplateId,
    swapTemplates,
  }),
  flattenProp('template'),
  withState('isDetailOpen', 'setDetailOpenState', false),
  withHandlers({
    toggleDropdown,
  }),
  withHandlers({
    openDetail,
    closeDetail,
    removeTemplate,
    duplicateTemplate,
    removeDuplicate,
  }),
  withStateHandlers(
    { selectedDesignOption: null },
    { closeDesign, onClickOrTouch, expandMiniMore },
  ),
  withStateHandlers(
    { multiplePriorities: false },
    { openMultiplePriorities, closeMultiplePriorities },
  ),
  withStateHandlers(
    { submissionNotificationOpen: false },
    { toggleSubmissionNote },
  ),
  addProps({
    priceBand,
  }),
  addProps({
    priceBandMax,
    priceBandMin,
    primaryOptions,
  }),
  withStateHandlers(
    { expanded: false },
    {
      expand,
      collapse,
    },
  ),
  addProps({
    formattedTotal,
    formattedDesignPrice,
    formattedPriceBandMax,
    formattedPriceBandMin,
    renderBmwOrMotorradSelection,
    renderMiniSelection,
    hasSubmitted,
    displayPrioritiesExpanded,
    inReview,
    inCart,
    zeroQuantity,
    isDropdownOpen,
    dropDownModelId,
    dropdownDisabled,
    secondaryOptions,
  }),
  withHandlers({
    closeSelection,
    collapseAndOpenDesign,
  }),
  addProps({
    formattedPriceBand,
  }),
);
